import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=user
  const [ready, setReady] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Iter 45.23 — if the backend returned "Account has been deactivated"
      // while a session was still open, surface that to the user before
      // silently bumping them back to /login. Every other 401 (expired
      // token, no cookie) uses the plain "signed out" path.
      const detail = err?.response?.data?.detail || "";
      const status = err?.response?.status;
      setUser((prev) => {
        if (prev && prev.id && status === 401 && String(detail).toLowerCase().includes("deactivat")) {
          try { toast.error("Your account has been deactivated. Please contact your admin."); } catch {}
        }
        return false;
      });
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Re-fetch when the tab regains focus so role/department changes made by
  // an admin propagate without requiring a manual re-login.
  useEffect(() => {
    const onFocus = () => { fetchMe(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchMe]);

  // Iter 45.23 — poll /auth/me every 60s so an admin-triggered deactivation
  // force-logs the user out of any currently-open tab, even one that
  // never regains focus (e.g. a spreadsheet left open on a second monitor).
  useEffect(() => {
    if (!user || !user.id) return; // only poll while signed in
    const t = setInterval(() => { fetchMe(); }, 60000);
    return () => clearInterval(t);
  }, [user, fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Auth is carried by the httpOnly cookie the server set on this
    // response — nothing sensitive is stored in localStorage.
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, ready, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
